import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import {
  Mutation,
  MutationAddRestApiKeyArgs,
  MutationDeleteRestApiKeyArgs,
  MutationUpdateRestApiKeyArgs,
  Query,
  RestApiKey
} from 'graphql/generated/graphqlTypes';
import { ADD_REST_API_KEY, DELETE_REST_API_KEY, UPDATE_REST_API_KEY } from 'graphql/v2/mutations';
import { GET_REST_API_KEYS } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect } from 'react';
import { TranslatedHookProps } from '../hooks.types';

const useApiKeys = ({ translation, handleFetchError }: TranslatedHookProps) => {
  const { loading, error, data, networkStatus } = useQuery<Pick<Query, 'getRestApiKeys'>>(GET_REST_API_KEYS, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (error) {
      handleFetchError(translation.api_key_error_fetching);
      console.error(error);
    }
  }, [error]);

  const isRefetchingApiKeys = !!data && networkStatus !== NetworkStatus.ready;

  useRegisterBackgroundRefetch(isRefetchingApiKeys);

  const [addRestApiKeyMutation] = useMutation<Pick<Mutation, 'addRestApiKey'>, MutationAddRestApiKeyArgs>(
    ADD_REST_API_KEY,
    {
      refetchQueries: [{ query: GET_REST_API_KEYS }],
      awaitRefetchQueries: true,
      onError: () => handleFetchError(translation.api_key_error_adding)
    }
  );

  const [updateRestApiKeyMutation] = useMutation<Pick<Mutation, 'updateRestApiKey'>, MutationUpdateRestApiKeyArgs>(
    UPDATE_REST_API_KEY,
    {
      refetchQueries: [{ query: GET_REST_API_KEYS }],
      awaitRefetchQueries: true
    }
  );

  const [deleteRestApiKeyMutation] = useMutation<Pick<Mutation, 'deleteRestApiKey'>, MutationDeleteRestApiKeyArgs>(
    DELETE_REST_API_KEY,
    {
      refetchQueries: [{ query: GET_REST_API_KEYS }],
      awaitRefetchQueries: true
    }
  );

  const addApiKey = async (description: string): Promise<RestApiKey | undefined> => {
    const { data: mutationData } = await addRestApiKeyMutation({ variables: { description } });
    return mutationData?.addRestApiKey;
  };

  const updateApiKey = async (id: string, description: string): Promise<boolean> => {
    try {
      const { data: mutationData } = await updateRestApiKeyMutation({ variables: { id, description } });
      return !!mutationData?.updateRestApiKey;
    } catch {
      return false;
    }
  };

  const deleteApiKey = async (id: string): Promise<boolean> => {
    try {
      const { data: mutationData } = await deleteRestApiKeyMutation({ variables: { id } });
      return !!mutationData?.deleteRestApiKey;
    } catch {
      return false;
    }
  };

  return {
    restApiKeys: data?.getRestApiKeys ?? [],
    loading: loading && !data,
    isRefetchingApiKeys,
    addApiKey,
    updateApiKey,
    deleteApiKey
  };
};

export default useApiKeys;
